import {noop} from '@benchling/js-utils';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import BenchlingConfig from 'BenchlingConfig';
import {FixedPlateModalType} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {
  ActionOrigin,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
  STORABLE_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {AccessibleGridCell, FullyAccessibleGrid} from 'constants/SampleConstants';
import notifications from 'notifications';
import {
  getInventoryRegistryChangeSummary,
  InventoryRegistryDataModAction,
} from 'samples/getInventoryRegistryChangeSummary';
import {fillFixedPlate, FixedPlateTransferInputWell} from 'samples/GridDAO';
import {usePlateMapFromGrid} from 'samples/grids/hooks/usePlateMapFromGrid';
import {
  getAverageContentsAddedPerWell,
  getModifiedStorableIdsForTransfer,
  getTransferToFixedPlateInputsFromPlateMap,
} from 'samples/plate-maps/AnnotateFillPlateUtil';
import FillPlateModal from 'samples/plate-maps/fill-plate-modal/FillPlateModal';
import {PlateMap, PlateModalAnalyticsMetadata} from 'samples/plate-maps/PlateMapConstants';
import {getCellsAsIndices} from 'samples/SampleUtil';
import {bulkFetchStorables} from 'samples/StorableActions';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';
import {withLoadingScreen} from 'util/Promises';

export interface GridCellToolbarFillFixedPlateButtonProps {
  grid: FullyAccessibleGrid;
  selectedCells: Array<AccessibleGridCell>;
}

/**
 * Button for opening the fill well plate modal flow.
 */
export default function GridCellToolbarFillFixedPlateButton({
  grid,
  selectedCells,
}: GridCellToolbarFillFixedPlateButtonProps): JSX.Element | null {
  const {plateMap, isPending} = usePlateMapFromGrid(grid);

  async function fillPlateAndRefetch({
    callbackOnError,
    dataModsHeader,
    eventGroupId,
    transferInputs,
    updatedPlateMap,
  }: {
    callbackOnError: () => void;
    dataModsHeader?: DataModificationsHeader;
    eventGroupId: string;
    transferInputs: Array<FixedPlateTransferInputWell>;
    updatedPlateMap: PlateMap;
  }): Promise<void> {
    try {
      await fillFixedPlate(updatedPlateMap.id!, {transferInputs}, dataModsHeader);
    } catch (err) {
      callbackOnError();
      throw err;
    }
    notifications.success('Filled plate');
    const averageContentsAddedPerWell = getAverageContentsAddedPerWell(updatedPlateMap);
    const analyticsMetadata: PlateModalAnalyticsMetadata = {
      actionOrigin: ActionOrigin.INVENTORY_PLATE_PAGE_GRID_CELL_TOOLBAR,
      modalType: FixedPlateModalType.FILL_PLATE,
      plateId: grid.id,
      averageContentsAdded: averageContentsAddedPerWell,
      eventGroupId,
    };
    analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.SAVED_FIXED_PLATE_FROM_MODAL, analyticsMetadata);
    await bulkFetchStorables([grid.id]);
  }

  async function fillPlateWithDataMods({
    callbackOnError,
    eventGroupId,
    updatedPlateMap,
  }: {
    callbackOnError: () => void;
    eventGroupId: string;
    updatedPlateMap: PlateMap;
  }): Promise<void> {
    const transferInputs = getTransferToFixedPlateInputsFromPlateMap(updatedPlateMap);
    const storableIds = getModifiedStorableIdsForTransfer(updatedPlateMap, grid, true);
    // If nothing has been modified, then short-circuit; nothing to update here and
    // getInventoryRegistryChangeSummary will error in this case anyways
    if (storableIds.length === 0) {
      return;
    }

    await callAsyncWithDataModifications({
      changeSummary: getInventoryRegistryChangeSummary({
        apiIds: storableIds,
        action: InventoryRegistryDataModAction.TRANSFER,
        containersMayBeWells: true,
      }),
      onModalCancel: noop,
      onPerformAction: async (dataModsHeader?) => {
        await withLoadingScreen(
          fillPlateAndRefetch({
            callbackOnError,
            dataModsHeader,
            eventGroupId,
            transferInputs,
            updatedPlateMap,
          }),
          'Filling plate...'
        );
      },

      analyticsParams: {
        module: 'SAMPLES',
        actionOrigin: ActionOrigin.INVENTORY_PLATE_PAGE_GRID_CELL_TOOLBAR,
        modifiedAction: InventoryRegistryDataModAction.TRANSFER,
        numModifiedTotalItems: storableIds.length,
        entryId: null,
      },
      apiIds: storableIds,
      useProjectLevelDataModsLogic: BenchlingConfig.AUDITING_DATA_MODIFICATIONS_V2_INVENTORY,
    });
  }

  function runFillPlateModal(): void {
    const eventGroupId = analytics.makeAnalyticsEventId();
    const analyticsMetadata: PlateModalAnalyticsMetadata = {
      actionOrigin: ActionOrigin.INVENTORY_PLATE_PAGE_GRID_CELL_TOOLBAR,
      modalType: FixedPlateModalType.FILL_PLATE,
      plateId: grid.id,
      eventGroupId,
    };
    analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.OPENED_MODAL,
      ANALYTICS_MODULE.SAMPLES,
      analyticsMetadata
    );
    runModal(FillPlateModal, {
      initialPlateMap: plateMap,
      selectedWellIndices: getCellsAsIndices(selectedCells, grid),
      fillPlate: async (callbackOnError: () => void, plateMap: PlateMap) => {
        await fillPlateWithDataMods({callbackOnError, eventGroupId, updatedPlateMap: plateMap});
      },
      containerSchemaId: grid.storableSchema.containerSchemaId!,
      eventGroupId,
      onRequestClose: () => {
        analytics.trackEventWithModule(
          COMMON_ANALYTICS_ACTIONS.CLOSED_MODAL,
          ANALYTICS_MODULE.SAMPLES,
          analyticsMetadata
        );
      },
    });
  }

  return (
    <Button
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      onClick={runFillPlateModal}
      size={ButtonSize.SMALL}
      text="Fill wells"
      disabled={isPending}
      dataTestAttrs={{
        component: 'Button',
        key: 'fillWells',
      }}
    />
  );
}
